import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import AddSleep from './AddSleep';
import AddWakeup from './AddWakeup';
import {
  isEnableLoadingSelector,
  sleepListSelector,
} from './selector';
import { deleteSleep, getSleepList, refreshSleepList } from './thunk';

import type { TableBaseColumnType, TableBaseRowType } from '@/components/TableBase/Body';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { FacilityJson } from '@/types/domain/childApply/entities/facilityEntity';
import type { SleepJson } from '@/types/domain/sleep/entities/sleepEntity';
import type { UserType } from '@/types/domain/user/redux';

import AdditionalLoading from '@/components/AdditionalLoading';
import Delete from '@/components/Delete';
import TableBase from '@/components/TableBase';
import SleepInfo from '@/domain/sleep/component/SleepInfo';
import { diffHoursMinutes, toStringDateTimeDisplay } from '@/utils/date';

/**
 * プロパティ
 */
export type Props = {
  /** ユーザー */
  user: UserType,
  /** 子ども */
  child: ChildJson,
  /** 施設リスト */
  facilityList: FacilityJson[],
};

const Sleep: React.FC<Props> = ({ user, child, facilityList }) => {
  const dispatch = useDispatch();
  const isEnableLoading = useSelector(isEnableLoadingSelector);
  const [
    isOpen,
    setIsOpen,
  ] = React.useState<boolean>(false);
  const [
    isSleepTime,
    setIsSleepTime,
  ] = React.useState<boolean>(false);
  const sleepList = useSelector(sleepListSelector);
  const [
    deleteSleepJson,
    setDeleteSleepJson,
  ] = React.useState<SleepJson | undefined>(undefined);
  const rows: TableBaseRowType[] = [];
  for (let i = 0; i < sleepList.length; i += 1) {
    if (sleepList[i].data.childId === child.childId) {
      const dt = new Date(sleepList[i].data.measureDateTime);
      let time = '↑';
      if (!sleepList[i].data.isSleepTime && sleepList[i + 1]
        && sleepList[i + 1].data.isSleepTime) {
        // 起床時間に睡眠時間を記載
        const sleepTime = new Date(sleepList[i + 1].data.measureDateTime);
        time = diffHoursMinutes(sleepTime, dt);
      } else if (sleepList[i].data.isSleepTime && !sleepList[i - 1]) {
        // 就寝後記録なし
        const now = new Date();
        time = diffHoursMinutes(dt, now);
      }
      rows.push({
        id: i.toString(),
        name: sleepList[i].data.isSleepTime ? '就寝' : '起床',
        measureDateTime: toStringDateTimeDisplay(dt, false),
        type: sleepList[i].data.measurerType,
        time,
      });
    }
  }
  const columns: TableBaseColumnType[] = [
    { id: 'name', label: '睡眠', minWidth: 30 },
    { id: 'measureDateTime', label: '時間', minWidth: 110 },
    { id: 'time', label: '睡眠時間', minWidth: 70 },
  ];
  const actions = [];
  actions.push({
    icon: <DeleteIcon />,
    title: '削除',
    action: (r: TableBaseRowType) => {
      const item = sleepList[parseInt(r.id, 10)];
      setDeleteSleepJson(item);
    },
    disabled: (r: TableBaseRowType) => r.type !== 'app',
  });
  return (
    <div>
      <div>
        <div>
          <Button
            variant="contained"
            onClick={() => {
              setIsSleepTime(true);
              setIsOpen(true);
            }}
            style={{ marginBottom: '5px' }}
          >
            就寝登録
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setIsSleepTime(false);
              setIsOpen(true);
            }}
            style={{ marginBottom: '5px', marginLeft: '5px' }}
          >
            起床登録
          </Button>
        </div>
        <TableBase
          rows={rows}
          columns={columns}
          actions={actions}
          refreshAction={() => dispatch(refreshSleepList())}
          isPagination={false}
          isShowFilter={false}
          isShowFooter={false}
        />
        <AdditionalLoading
          isEnableLoading={isEnableLoading}
          onClick={() => dispatch(getSleepList())}
        />
        {isSleepTime && (
          <AddSleep
            isOpen={isOpen}
            close={() => setIsOpen(false)}
            user={user}
            child={child}
            facilityList={facilityList}
            isYesterday={false}
          />
        )}
        {!isSleepTime && (
          <AddWakeup
            isOpen={isOpen}
            close={() => setIsOpen(false)}
            user={user}
            child={child}
            facilityList={facilityList}
          />
        )}
        <Delete<SleepJson>
          comp={SleepInfo}
          data={deleteSleepJson}
          onDelete={(json) => {
            if (json) {
              dispatch(deleteSleep({
                data: json,
                facilityIds: facilityList.map((v) => v.data.facilityId),
              }));
            }
            setDeleteSleepJson(undefined);
          }}
          title="睡眠の記録"
          content="以下の記録を削除しますか？"
        />
      </div>
    </div>
  );
};

export default Sleep;
